import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { CheckCircle, FileText, Loader2, Shield } from 'lucide-react'
import type { LegalDocument, LegalDocumentType } from '@/shared/domain'
import { formatDate } from '@/shared/infra/format'
import { Button, Card, CardContent, CardFooter, CardHeader, Checkbox, Label } from '@/shared/ui/primitives'
import { cn } from '@/shared/ui/cn'
import { useLegalAcceptanceModal } from './use-legal-acceptance-modal'

export type LegalAcceptanceModalProps = {
  documents: LegalDocument[]
  onAccept: (documents: LegalDocument[]) => Promise<void>
  isOpen: boolean
}

const DOCUMENT_TYPE_LABELS: Record<LegalDocumentType, string> = {
  terms_of_service: 'Terms of Service',
  privacy_policy: 'Privacy Policy',
  data_processing_agreement: 'Data Processing Agreement',
}

function preventClose(event: Event): void {
  event.preventDefault()
}

export function LegalAcceptanceModal({ documents, onAccept, isOpen }: LegalAcceptanceModalProps) {
  const vm = useLegalAcceptanceModal({ documents, onAccept })
  const allScrolled = documents.every((doc) => vm.scrolledToEnd[doc.documentId] === true)
  const allAccepted = documents.every((doc) => vm.accepted[doc.documentId] === true)

  return (
    <DialogPrimitive.Root open={isOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          role="alertdialog"
          aria-labelledby="legal-acceptance-modal-title"
          aria-describedby="legal-acceptance-modal-description"
          onPointerDownOutside={preventClose}
          onEscapeKeyDown={preventClose}
          onInteractOutside={preventClose}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[95vw] -translate-x-1/2 -translate-y-1/2 flex-col gap-0 border bg-background text-foreground shadow-lg sm:rounded-lg sm:max-w-2xl"
        >
          <header className="flex shrink-0 items-start gap-3 border-b px-6 py-5">
            <Shield aria-hidden className="mt-1 size-6 text-primary" />
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title
                id="legal-acceptance-modal-title"
                className="text-lg font-semibold leading-tight tracking-tight"
              >
                Legal Agreement Required
              </DialogPrimitive.Title>
              <DialogPrimitive.Description
                id="legal-acceptance-modal-description"
                className="mt-1 text-sm text-muted-foreground"
              >
                Please review and accept our legal documents to continue using the platform.
              </DialogPrimitive.Description>
            </div>
          </header>

          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
            {documents.map((document) => {
              const isScrolled = vm.scrolledToEnd[document.documentId] === true
              const isAccepted = vm.accepted[document.documentId] === true
              const displayName = DOCUMENT_TYPE_LABELS[document.documentType] ?? 'Document'
              const checkboxId = `legal-accept-${document.documentId}`
              return (
                <Card key={document.documentId} className="gap-0 overflow-hidden py-0">
                  <CardHeader className="border-b py-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                          <FileText aria-hidden className="size-5" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold leading-tight">{displayName}</h3>
                          <p className="text-xs text-muted-foreground">
                            Version {document.version} · Effective{' '}
                            {formatDate(document.effectiveDate)}
                          </p>
                        </div>
                      </div>
                      {isAccepted ? (
                        <span className="inline-flex items-center gap-1.5 rounded-md bg-sage-soft px-2 py-1 text-xs font-medium text-sage">
                          <CheckCircle className="size-3.5" />
                          <span className="hidden sm:inline">Accepted</span>
                        </span>
                      ) : null}
                    </div>
                  </CardHeader>

                  <CardContent
                    onScroll={vm.handleScroll(document.documentId)}
                    className="h-48 overflow-y-auto bg-muted/30 py-4 lg:h-64"
                  >
                    <div className="prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-p:text-xs prose-p:leading-relaxed prose-li:text-xs prose-strong:text-foreground prose-a:text-primary prose-a:underline prose-a:underline-offset-2 lg:prose-p:text-sm lg:prose-li:text-sm">
                      <Markdown remarkPlugins={[remarkGfm]}>{document.content}</Markdown>
                    </div>
                  </CardContent>

                  {!isScrolled ? (
                    <p className="border-y bg-muted px-6 py-2 text-center text-xs font-medium text-muted-foreground">
                      Scroll to the end to accept this document
                    </p>
                  ) : null}

                  <CardFooter className="border-t py-3">
                    <Label
                      htmlFor={checkboxId}
                      className={cn(
                        'flex items-start gap-3',
                        isScrolled ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
                      )}
                    >
                      <Checkbox
                        id={checkboxId}
                        className="mt-0.5"
                        checked={isAccepted}
                        disabled={!isScrolled}
                        onCheckedChange={(value) =>
                          vm.handleCheckedChange(document.documentId, value === true)
                        }
                      />
                      <span className="flex-1 text-sm font-medium">
                        I have read and accept the {displayName}
                      </span>
                    </Label>
                  </CardFooter>
                </Card>
              )
            })}
          </div>

          <footer className="flex shrink-0 flex-col-reverse gap-3 border-t bg-muted/40 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              {vm.error ? (
                <p
                  role="alert"
                  className="flex items-center gap-2 text-xs font-medium text-destructive lg:text-sm"
                >
                  <span className="size-1.5 rounded-full bg-destructive" />
                  {vm.error}
                </p>
              ) : !allScrolled ? (
                <p className="text-xs text-muted-foreground lg:text-sm">
                  {documents.length === 1
                    ? 'Scroll to the end to continue'
                    : 'Scroll to the end of all documents to continue'}
                </p>
              ) : !allAccepted ? (
                <p className="text-xs text-muted-foreground lg:text-sm">
                  {documents.length === 1
                    ? 'Accept the document to continue'
                    : 'Accept all documents to continue'}
                </p>
              ) : (
                <p className="flex items-center gap-2 text-xs font-medium text-sage lg:text-sm">
                  <CheckCircle className="size-4" />
                  All documents accepted — ready to proceed
                </p>
              )}
            </div>
            <Button
              type="button"
              size="lg"
              onClick={vm.handleSubmit}
              disabled={!vm.canSubmit}
              className="w-full sm:w-auto"
            >
              {vm.isSubmitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Submitting…
                </>
              ) : (
                'Accept and Continue'
              )}
            </Button>
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
